// import React, { useState, useEffect } from "react";
// import { useNetworkStatus } from "./utils/userNetworkStatus.js";
// import { useUserLocation } from "./utils/useUserLocation.js";
// import { motion } from "framer-motion";
// import { CartProvider } from "./utils/cartContext.js";

// import Header from "./files/Header.jsx";
// import HomeScreen from "./files/HomeScreen/HomeScreen.jsx";
// import ServicesScreen from "./files/ServiceScreen/ServiceScreen.jsx";
// import ShopsScreen from "./files/Shops/ShopScreen.jsx";
// import CourierScreen from "./files/PortalScreen/PortalScreen.jsx";
// import OfflineBanner from "./components/OfflineScreen.jsx";
// import "./App.css";

// function App() {
//   // Network status hook to check if user is online
//   const isOnline = useNetworkStatus();

//   // Custom hook to get user's location with error and loading states
//   const {
//     location: currentLocation, // Renamed to avoid confusion with state
//     error: locationError,
//     isLoading: isLocationLoading,
//     updateLocation, // Added updateLocation function from hook
//   } = useUserLocation();

//   // State for controlling location modal visibility
//   const [showModal, setShowModal] = useState(false);

//   // State for storing user location data
//   const [userLocation, setUserLocation] = useState({
//     coordinates: {
//       latitude: 0,
//       longitude: 0,
//     },
//     address: "",
//     details: null,
//   });

//   // State for selected category with localStorage persistence
//   const [selectedCategory, setSelectedCategory] = useState(() => {
//     return localStorage.getItem("selectedCategory") || "products";
//   });

//   // Effect to persist selected category to localStorage
//   useEffect(() => {
//     localStorage.setItem("selectedCategory", selectedCategory);
//   }, [selectedCategory]);

//   // Effect to sync location from hook to component state
//   useEffect(() => {
//     if (currentLocation) {
//       setUserLocation({
//         coordinates: {
//           latitude: currentLocation.coordinates.latitude ,
//           longitude: currentLocation.coordinates.longitude,
//         },
//         address: currentLocation.address || "",
//         details: currentLocation.details || null,
//       });
//     }
//   }, [currentLocation]);

//   // Handler for modal close with location update
//   const handleModalClose = (shouldClose, newCoordinates = null) => {
//     setShowModal(false);
//     if (newCoordinates) {
//       // Update location through the hook's update function
//       updateLocation({
//         coordinates: {
//           latitude: newCoordinates.latitude,
//           longitude: newCoordinates.longitude,
//         },
//         address: newCoordinates.address || "",
//         details: newCoordinates.details || null,
//       });
//     }
//   };

//   // Render appropriate screen based on selected category
//   const renderScreen = () => {
//     // Check if we have valid coordinates before rendering screens
//     if (
//       !userLocation.coordinates ||
//       (userLocation.coordinates.latitude === 0 &&
//         userLocation.coordinates.longitude === 0)
//     ) {
//       return (
//         <div className="location-required">
//           <p>
//             Please enable location services or select a location to continue
//           </p>
//           <button onClick={() => setShowModal(true)}>Select Location</button>
//         </div>
//       );
//     }

//     // Screen components with proper error boundaries
//     switch (selectedCategory) {
//       case "services":
//         return (
//           <ServicesScreen
//             latitude={userLocation.coordinates.latitude}
//             longitude={userLocation.coordinates.longitude}
//             key={`services-${userLocation.coordinates.latitude}-${userLocation.coordinates.longitude}`}
//           />
//         );
//       case "shops":
//         return (
//           <ShopsScreen
//             latitude={userLocation.coordinates.latitude}
//             longitude={userLocation.coordinates.longitude}
//             key={`shops-${userLocation.coordinates.latitude}-${userLocation.coordinates.longitude}`}
//           />
//         );
//       case "courier":
//         return (
//           <CourierScreen
//             latitude={userLocation.coordinates.latitude}
//             longitude={userLocation.coordinates.longitude}
//             key={`courier-${userLocation.coordinates.latitude}-${userLocation.coordinates.longitude}`}
//           />
//         );
//       case "products":
//       default:
//         return (
//           <HomeScreen
//             latitude={userLocation.coordinates.latitude}
//             longitude={userLocation.coordinates.longitude}
//             key={`products-${userLocation.coordinates.latitude}-${userLocation.coordinates.longitude}`}
//           />
//         );
//     }
//   };

//   return (
//     <CartProvider>
//       <div className="app-container">
//         {isLocationLoading ? (
//           // Loading state for location detection
//           <motion.div
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             className="location-loader"
//             style={{ textAlign: "center", marginTop: "30vh" }}
//           >
//             <p>🔄 Detecting your location...</p>
//           </motion.div>
//         ) : (
//           // Main app content when location is loaded
//           <>
//             <Header
//               userLocation={userLocation}
//               setShowModal={setShowModal}
//               setUserLocation={updateLocation} // Using hook's update function
//               showModal={showModal}
//               selectedCategory={selectedCategory}
//               setSelectedCategory={setSelectedCategory}
//               hasShowOptions={true}
//             />
//             <main className="main-content">
//               {/* Show error if location access was denied */}
//               {locationError ? (
//                 <div className="location-error">
//                   <p>
//                     Location access denied. Please enable location services.
//                   </p>
//                   <button onClick={() => updateLocation(null)}>
//                     Try Again
//                   </button>
//                 </div>
//               ) : (
//                 renderScreen()
//               )}
//             </main>
//           </>
//         )}
//         {/* Show offline banner when not connected */}
//         {!isOnline && <OfflineBanner />}
//       </div>
//     </CartProvider>
//   );
// }

// export default App;




import React, { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { useNetworkStatus } from "./utils/userNetworkStatus.js";
import { useUserLocation } from "./utils/useUserLocation.js";
import { motion } from "framer-motion";
import { CartProvider } from "./utils/cartContext.js";
import Header from "./files/Header.jsx";
import OfflineBanner from "./components/OfflineScreen.jsx";
import "./App.css";

// Lazy load screen components for better performance
const HomeScreen = lazy(() => import("./files/HomeScreen/HomeScreen.jsx"));
const ServicesScreen = lazy(() =>
  import("./files/ServiceScreen/ServiceScreen.jsx")
);
const ShopsScreen = lazy(() => import("./files/Shops/ShopScreen.jsx"));
const CourierScreen = lazy(() =>
  import("./files/PortalScreen/PortalScreen.jsx")
);

// Screen loading fallback component
const ScreenLoader = () => (
  <div className="screen-loader">
    <p>Loading content...</p>
  </div>
);

// Location Required Component
const LocationRequired = ({ onSelectLocation }) => (
  <div className="location-required">
    <p>Please enable location services or select a location to continue</p>
    <button onClick={onSelectLocation}>Select Location</button>
  </div>
);

// Location Error Component
const LocationError = ({ onRetry }) => (
  <div className="location-error">
    <p>Location access denied. Please enable location services.</p>
    <button onClick={onRetry}>Try Again</button>
  </div>
);

// Location Loader Component
const LocationLoader = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="location-loader"
    style={{ textAlign: "center", marginTop: "30vh" }}
  >
    <p>🔄 Detecting your location...</p>
  </motion.div>
);

function App() {
  const isOnline = useNetworkStatus();
  const {
    location: currentLocation,
    error: locationError,
    isLoading: isLocationLoading,
    updateLocation,
  } = useUserLocation();

  const [showModal, setShowModal] = useState(false);
  const [userLocation, setUserLocation] = useState({
    coordinates: { latitude: 0, longitude: 0 },
    address: "",
    details: null,
  });

  // Initialize selected category from localStorage with proper error handling
  const [selectedCategory, setSelectedCategory] = useState("products");

  // Initialize category from localStorage safely
  useEffect(() => {
    try {
      const savedCategory = localStorage.getItem("selectedCategory");
      if (savedCategory) {
        setSelectedCategory(savedCategory);
      }
    } catch (error) {
    }
  }, []);

  // Persist selected category to localStorage with error handling
  useEffect(() => {
    try {
      localStorage.setItem("selectedCategory", selectedCategory);
    } catch (error) {
    }
  }, [selectedCategory]);

  // Sync location from hook to component state
  useEffect(() => {
    if (currentLocation) {
      setUserLocation({
        coordinates: {
          latitude: currentLocation.coordinates.latitude,
          longitude: currentLocation.coordinates.longitude,
        },
        address: currentLocation.address || "",
        details: currentLocation.details || null,
      });
    }
  }, [currentLocation]);

  // Memoized handler for modal close
  const handleModalClose = useCallback(
    (shouldClose, newCoordinates = null) => {
      setShowModal(false);
      if (newCoordinates) {
        updateLocation({
          coordinates: {
            latitude: newCoordinates.latitude,
            longitude: newCoordinates.longitude,
          },
          address: newCoordinates.address || "",
          details: newCoordinates.details || null,
        });
      }
    },
    [updateLocation]
  );

  // Check if we have valid coordinates
  const hasValidLocation =
    userLocation.coordinates &&
    userLocation.coordinates.latitude !== 0 &&
    userLocation.coordinates.longitude !== 0;

  // Memoized screen renderer with proper keys for component reuse
  const renderScreen = useCallback(() => {
    if (!hasValidLocation) {
      return <LocationRequired onSelectLocation={() => setShowModal(true)} />;
    }

    const screenProps = {
      latitude: userLocation.coordinates.latitude,
      longitude: userLocation.coordinates.longitude,
      key: `${selectedCategory}-${userLocation.coordinates.latitude}-${userLocation.coordinates.longitude}`,
    };

    switch (selectedCategory) {
      case "services":
        return (
          <Suspense fallback={<ScreenLoader />}>
            <ServicesScreen {...screenProps} />
          </Suspense>
        );
      case "shops":
        return (
          <Suspense fallback={<ScreenLoader />}>
            <ShopsScreen {...screenProps} />
          </Suspense>
        );
      case "courier":
        return (
          <Suspense fallback={<ScreenLoader />}>
            <CourierScreen {...screenProps} />
          </Suspense>
        );
      case "products":
      default:
        return (
          <Suspense fallback={<ScreenLoader />}>
            <HomeScreen {...screenProps} />
          </Suspense>
        );
    }
  }, [selectedCategory, userLocation, hasValidLocation]);

  return (
    <CartProvider>
      <div
        className="app-container"
        itemScope
        itemType="https://schema.org/WebApplication"
      >
        <meta itemProp="name" content="Local Services App" />
        <meta
          itemProp="description"
          content="Find products, services, shops and courier services near you"
        />

        {isLocationLoading ? (
          <LocationLoader />
        ) : (
          <>
            <Header
              userLocation={userLocation}
              setShowModal={setShowModal}
              setUserLocation={updateLocation}
              showModal={showModal}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              hasShowOptions={true}
            />

            <main
              className="main-content"
              role="main"
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/WebPageElement"
            >
              <h1 className="seo-heading" itemProp="headline">
                {selectedCategory === "products" }
                {selectedCategory === "services" }
                {selectedCategory === "shops" }
                {selectedCategory === "courier"}
              </h1>

              {locationError ? (
                <LocationError onRetry={() => updateLocation(null)} />
              ) : (
                renderScreen()
              )}
            </main>
          </>
        )}

        {!isOnline && <OfflineBanner />}
      </div>
    </CartProvider>
  );
}

export default React.memo(App);